import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import Logo from "./Logo";
import { useSite } from "@/lib/SiteContext";
import { useCart } from "@/lib/CartContext";
import { track } from "@/lib/api";

export function kindleLink(volume) {
  const f = (volume?.formats || []).find((x) => x.key === "kindle" && x.status === "available");
  return f?.url || "";
}

export default function Navbar() {
  const { flags, volume } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/book", label: "The Book", tid: "nav-the-book-link" },
    { to: "/bookstore", label: "Official Bookstore", tid: "nav-bookstore-link" },
    { to: "/merch", label: "Official Merch", tid: "nav-merch-link" },
    flags.media > 0 && { to: "/cinema", label: "The Cinema", tid: "nav-cinema-link" },
    flags.daily > 0 && { to: "/daily", label: "The Daily Chronicle", tid: "nav-daily-link" },
    { to: "/author", label: "About the Author", tid: "nav-the-author-link" },
    { to: "/connect", label: "Contact", tid: "nav-connect-link" },
  ].filter(Boolean);

  const mobileLinks = [
    ...links,
    flags.excerpts > 0 && { to: "/chronicles", label: "Inside the Chronicles", tid: "nav-chronicles-link" },
    { to: "/appearances", label: "Appearances", tid: "nav-appearances-link" },
  ].filter(Boolean);

  const cart = useCart();
  const cartCount = cart?.count || 0;

  const buy = kindleLink(volume);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "backdrop-blur-md bg-ink/85 border-b border-paper/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-10 h-20" aria-label="Main">
        <Link to="/" data-testid="nav-home-link" aria-label="Dirty Church Chronicles home">
          <Logo />
        </Link>
        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.tid}
              className={({ isActive }) =>
                `font-ui text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                  isActive ? "text-crimsonhi" : "text-paper/70 hover:text-paper"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <NavLink to="/cart" data-testid="nav-cart-link" aria-label={`Shopping cart, ${cartCount} items`}
            className="relative text-paper/70 hover:text-paper transition-colors duration-300">
            <ShoppingBag size={19} />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2.5 bg-crimson text-paper text-[9px] font-semibold w-4.5 h-4.5 min-w-[18px] min-h-[18px] rounded-full flex items-center justify-center" data-testid="nav-cart-count">
                {cartCount}
              </span>
            )}
          </NavLink>
          {buy && (
            <a
              href={buy}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-get-book-btn"
              onClick={() => track("purchase_click", "nav")}
              className="font-ui text-[11px] uppercase tracking-[0.22em] font-semibold bg-crimson text-paper px-5 py-2.5 rounded-full transition-[background-color,transform] duration-300 hover:bg-crimsonhi hover:scale-[1.03] active:scale-[0.98]"
            >
              Get the Book
            </a>
          )}
        </div>
        <div className="flex items-center gap-1 lg:hidden">
          <NavLink to="/cart" data-testid="mobile-cart-link" aria-label={`Shopping cart, ${cartCount} items`}
            className="relative text-paper p-2">
            <ShoppingBag size={22} />
            {cartCount > 0 && (
              <span className="absolute top-0 right-0 bg-crimson text-paper text-[9px] font-semibold min-w-[16px] min-h-[16px] rounded-full flex items-center justify-center" data-testid="mobile-cart-count">
                {cartCount}
              </span>
            )}
          </NavLink>
          <button
            className="text-paper p-2"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-menu-btn"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden fixed inset-0 top-20 bg-ink/97 backdrop-blur-xl z-40"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-2 px-8 pt-10">
              {mobileLinks.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-${l.tid}`}
                    className="block font-display text-3xl text-paper py-3 border-b border-paper/10"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              {buy && (
                <a
                  href={buy}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-get-book-btn"
                  onClick={() => track("purchase_click", "mobile-nav")}
                  className="mt-8 text-center font-ui text-xs uppercase tracking-[0.25em] font-semibold bg-crimson text-paper px-6 py-4 rounded-full"
                >
                  Get the Book on Amazon
                </a>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
